import React from 'react';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';
import {
  PURCHASE_REQUEST_STATUS_CONFIG,
  NFA_STATUS_CONFIG,
  LFA_STATUS_CONFIG,
  RFQ_STATUS_CONFIG,
  PO_STATUS_CONFIG,
  GRN_STATUS_CONFIG,
  INVOICE_STATUS_CONFIG,
  PAYMENT_STATUS_CONFIG,
} from '@/constants/status';

export type StatusCategory =
  | 'PurchaseRequest'
  | 'NFA'
  | 'LFA'
  | 'RFQ'
  | 'PO'
  | 'GRN'
  | 'Invoice'
  | 'Payment';

interface StatusBadgeProps {
  status: string;
  category: StatusCategory;
  className?: string;
  showDot?: boolean;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, category, className, showDot = true }) => {
  let config: { label: string; variant: 'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'outline' | 'copper' } = {
    label: status,
    variant: 'secondary',
  };

  switch (category) {
    case 'PurchaseRequest':
      if (status in PURCHASE_REQUEST_STATUS_CONFIG) {
        config = PURCHASE_REQUEST_STATUS_CONFIG[status as keyof typeof PURCHASE_REQUEST_STATUS_CONFIG];
      }
      break;
    case 'NFA':
      if (status in NFA_STATUS_CONFIG) {
        config = NFA_STATUS_CONFIG[status as keyof typeof NFA_STATUS_CONFIG];
      }
      break;
    case 'LFA':
      if (status in LFA_STATUS_CONFIG) {
        config = LFA_STATUS_CONFIG[status as keyof typeof LFA_STATUS_CONFIG]!;
      }
      break;
    case 'RFQ':
      if (status in RFQ_STATUS_CONFIG) {
        config = RFQ_STATUS_CONFIG[status as keyof typeof RFQ_STATUS_CONFIG];
      }
      break;
    case 'PO':
      if (status in PO_STATUS_CONFIG) {
        config = PO_STATUS_CONFIG[status as keyof typeof PO_STATUS_CONFIG];
      }
      break;
    case 'GRN':
      if (status in GRN_STATUS_CONFIG) {
        config = GRN_STATUS_CONFIG[status as keyof typeof GRN_STATUS_CONFIG];
      }
      break;
    case 'Invoice':
      if (status in INVOICE_STATUS_CONFIG) {
        config = INVOICE_STATUS_CONFIG[status as keyof typeof INVOICE_STATUS_CONFIG];
      }
      break;
    case 'Payment':
      if (status in PAYMENT_STATUS_CONFIG) {
        config = PAYMENT_STATUS_CONFIG[status as keyof typeof PAYMENT_STATUS_CONFIG];
      }
      break;
  }

  let dotColor = 'bg-stone-400';
  if (config.variant === 'success') dotColor = 'bg-emerald-500';
  else if (config.variant === 'warning' || config.variant === 'copper') dotColor = 'bg-amber-600';
  else if (config.variant === 'destructive') dotColor = 'bg-rose-600';
  else if (config.variant === 'default') dotColor = 'bg-primary';

  return (
    <Badge variant={config.variant} className={cn('font-poppins select-none inline-flex items-center justify-center', className)}>
      {showDot && <span className={`mr-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${dotColor}`} />}
      <span className="truncate">{config.label}</span>
    </Badge>
  );
};
